import { useCallback } from 'react';
import { useMutation } from '@apollo/client';
import {
  IAnalysisPrepareCohortMutation,
  IAnalysisPrepareCohortMutationVariables,
} from '../apollo/graphqlSchema';
import { ANALYSIS_PREPARE_COHORT } from '../apollo/analysis';
import { useAppDispatch } from '../../redux/hooks';
import { populateBuilderInfoAction } from '../../redux/slices/analyses';
import printGraphqlErrors from '../apollo/printGraphqlErrors';
import { assertNotNil } from '../types';
import { useChartContext } from '../../components/Chart/ChartContext';

/**
 * This hook is going to help us create cohort with breakdown.
 * it use selected sliceKey as globalFilter to create a NEW ANALYSIS, and also drop off all breakdowns.
 * and also retrieve the measure id if create cohort on specific measure for query cohort.
 * @param analysisId
 */
const useAnalysisPrepareCohort = (analysisId: string) => {
  const dispatch = useAppDispatch();
  const {
    metadata: { query },
  } = useChartContext();
  const handleMutationComplete = ({
    analysisPrepareCohort,
  }: IAnalysisPrepareCohortMutation) => {
    const { errors, entity } = analysisPrepareCohort;

    if (printGraphqlErrors(errors)) {
      return;
    }

    assertNotNil(entity);
    dispatch(
      populateBuilderInfoAction({
        analysisInfo: entity.analysis,
      }),
    );
  };

  const [analysisPrepareCohort] = useMutation<
    IAnalysisPrepareCohortMutation,
    IAnalysisPrepareCohortMutationVariables
  >(ANALYSIS_PREPARE_COHORT, {
    onCompleted: handleMutationComplete,
  });

  return useCallback(
    (sliceKey: string, measureId?: string) => {
      assertNotNil(query.schema.id);

      return analysisPrepareCohort({
        variables: {
          input: {
            analysisId,
            breakdownEdit: {
              globalFilterSliceKeys: [sliceKey],
              removeAllBreakdowns: true,
            },
            expressionCohort: measureId
              ? {
                  measureId,
                }
              : null,
            schema: query.schema.id,
          },
        },
      });
    },
    [analysisPrepareCohort, analysisId, query],
  );
};

export default useAnalysisPrepareCohort;
